import * as React from 'react';

import {
    DataScopesEnum,
    EmptyGuid,
    FavoriteScopeEnum,
    FavoriteTypesEnum,
    UserRightsEnum,
} from '../../utilities/Constants';
import { ILookupModel, IMultiSelectOption } from '../../interfaces/ILookup';

import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from './dialog';
import CheckmateSelect from './CheckmateSelect';
import { IFavoriteModel } from '../../interfaces/IFavorite';
import { INoteUISearchModel } from '../../interfaces/IQuery';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Modal } from 'react-bootstrap';
import Sort from '../../stores/Sort';
import ValidateUtils from '../../shared/validations';
import { ZoneContextType } from '../../utilities/Constants';
import { cloneDeep } from 'lodash';
import { showSuccessToast } from '../../shared/ToastUtils';

interface IFavoriteProps {
    applyFavoriteSelected: (settingJson?: string, isDefault?: boolean) => void;
    currentFavoriteDetailsString: string;
    currentFavoriteSettingJson: string;
    onDefaultFavoriteChange?: (settingJson?: string) => void;
    onFavoriteSelected?: (settingJson?: string) => void;
    onValidateFilters?: () => boolean;
    type: ILookupModel;
    user: IUserModel;
    zoneContext: ZoneContextType;
}

interface IFavoriteState {
    favorites: IFavoriteModel[];
    newFavorite?: IFavoriteModel;
    pendingResponse: boolean;
    selectedFavoriteScope?: number;
    selectedUserFavoriteGuid?: string;
    selectedZoneFavoriteGuid?: string;
    showAddNewModal?: boolean;
    showDeleteFavoriteDialog?: boolean;
    showFavoriteDetailsDialog?: boolean;
    showFavoritesModal?: boolean;
    validation: IValidation;
}

export class Favorite extends React.Component<IFavoriteProps, IFavoriteState> {
    constructor(props: any) {
        super(props);
        this.state = {
            pendingResponse: false,
            validation: {},
            favorites: [],
        };
    }

    componentDidMount() {
        this.loadFavorites();
    }

    componentDidUpdate(prevProps: IFavoriteProps) {
        if (
            prevProps.currentFavoriteSettingJson != this.props.currentFavoriteSettingJson &&
            this.state.newFavorite
        ) {
            const newFavoriteCopy = cloneDeep(this.state.newFavorite);
            newFavoriteCopy.settingJson = this.props.currentFavoriteSettingJson;
            this.setState({ newFavorite: newFavoriteCopy });
        }
    }

    loadFavorites = async () => {
        const response = await this.props.zoneContext.zoneApiClient.getFavorites(
            this.props.type.id
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        this.setState({ favorites: response.payload! });
    };

    addNew = async () => {
        const newFavoriteCopy = cloneDeep(this.state.newFavorite);
        const validationCopy = cloneDeep(this.state.validation);

        if (!newFavoriteCopy) {
            validationCopy.model = ['Invalid Input'];
            this.setState({ validation: validationCopy });
            return;
        }

        if (!newFavoriteCopy.settingJson) {
            validationCopy.model = ['Favorite Setting Data is missing'];
            this.setState({ validation: validationCopy });
            return;
        }

        if (!newFavoriteCopy.name) {
            validationCopy.name = ['Name is Required'];
            this.setState({ validation: validationCopy });
            return;
        }

        const settings: INoteUISearchModel = JSON.parse(newFavoriteCopy.settingJson);
        if (settings.noteTypes?.length == 0) {
            validationCopy.name = ['At least one Type is required.'];
            this.setState({ validation: validationCopy });
            return;
        }

        const dataScopeIsMissingFourUserFavorite =
            newFavoriteCopy.favoriteScope != FavoriteScopeEnum.User &&
            (!newFavoriteCopy.dataScope || newFavoriteCopy.dataScope.id === 0);

        if (dataScopeIsMissingFourUserFavorite) {
            validationCopy.dataScope = ['Required'];
            this.setState({ validation: validationCopy });
            return;
        }

        this.setState({ pendingResponse: true });
        const response = await this.props.zoneContext.zoneApiClient.addFavorite(newFavoriteCopy);

        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        newFavoriteCopy.guid = response.payload!.data;
        let favoritesCopy = cloneDeep(this.state.favorites);

        if (newFavoriteCopy.default) {
            newFavoriteCopy.name += ' (Default)';
            favoritesCopy.map((item: IFavoriteModel) => {
                item.default = false;
                if (item.name) item.name = item.name.replace(' (Default)', '');
            });
        }

        favoritesCopy.push(cloneDeep(newFavoriteCopy));
        favoritesCopy = favoritesCopy.sort(Sort.compareValues('name'));

        showSuccessToast(
            <span>
                <strong>{newFavoriteCopy.name}</strong> was successfully saved
            </span>
        );

        this.setState(
            {
                pendingResponse: false,
                favorites: favoritesCopy,
                showFavoritesModal: false,
                newFavorite: undefined,
                showAddNewModal: false,
                validation: {},
            },
            () => {
                if (newFavoriteCopy.default) {
                    this.props.onDefaultFavoriteChange?.(newFavoriteCopy.settingJson);
                }
            }
        );
    };

    handleFavoriteSelected = () => {
        if (!this.state.selectedUserFavoriteGuid && !this.state.selectedZoneFavoriteGuid) {
            const validation = this.state.validation;
            validation.favorites = ['Please select a favorite'];
            this.setState({ validation: validation });
            return;
        }

        const match =
            this.state.selectedFavoriteScope === FavoriteScopeEnum.User
                ? this.state.favorites.find((x) => x.guid === this.state.selectedUserFavoriteGuid)
                : this.state.favorites.find((x) => x.guid === this.state.selectedZoneFavoriteGuid);
        if (match) {
            this.setState({ showFavoritesModal: false });
            this.props.applyFavoriteSelected(match.settingJson, match.default);
        }
    };

    handleChangeValue = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;

        let newFavoriteCopy = cloneDeep(this.state.newFavorite);
        const validationCopy = cloneDeep(this.state.validation);

        if (!newFavoriteCopy)
            newFavoriteCopy = {
                type: this.props.type,
                settingJson: this.props.currentFavoriteSettingJson,
                favoriteScope: FavoriteScopeEnum.User,
            };

        switch (name) {
            case 'name':
                if (value) validationCopy.name = [''];
                newFavoriteCopy.name = value;
                break;
            case 'description':
                newFavoriteCopy.description = value;
                break;
            default:
                break;
        }

        this.setState({ newFavorite: newFavoriteCopy, validation: validationCopy });
    };

    handleCheckedChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        let newFavoriteCopy = cloneDeep(this.state.newFavorite);

        if (!newFavoriteCopy) {
            newFavoriteCopy = {
                type: this.props.type,
                settingJson: this.props.currentFavoriteSettingJson,
                favoriteScope: FavoriteScopeEnum.User,
            };
        }

        switch (event.target.name) {
            case 'chkDefault':
                newFavoriteCopy.default = event.target.checked;
                break;
            default:
                break;
        }

        this.setState({ newFavorite: newFavoriteCopy });
    };

    enableDelete = () => {
        if (this.state.selectedUserFavoriteGuid) {
            const match = this.state.favorites.find(
                (x) => x.guid === this.state.selectedUserFavoriteGuid
            );
            if (match && match.favoriteScope === FavoriteScopeEnum.User) return true;
        } else if (this.state.selectedZoneFavoriteGuid) {
            const match = this.state.favorites.find(
                (x) => x.guid === this.state.selectedZoneFavoriteGuid
            );
            if (
                match &&
                match.favoriteScope === FavoriteScopeEnum.Zone &&
                Authorization.userHasRight(
                    UserRightsEnum.AddZoneFavorites,
                    this.props.user,
                    this.props.zoneContext.zoneGuid
                )
            )
                return true;
        }
        return false;
    };

    delete = async () => {
        const guid = this.state.selectedUserFavoriteGuid || this.state.selectedZoneFavoriteGuid;

        if (!guid) {
            const validation = this.state.validation;
            validation.favorites = ['Please select a favorite to delete'];
            this.setState({ validation: validation });
            return;
        }

        const response = await this.props.zoneContext.zoneApiClient.deleteFavorite(guid);
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
                showDeleteFavoriteDialog: false,
            });
            return;
        }

        const favorites = this.state.favorites;
        const matchIndex = favorites.findIndex((x) => x.guid === guid);
        const match = favorites.find((x) => x.guid === guid);
        if (matchIndex >= 0) {
            favorites.splice(matchIndex, 1);
            this.setState({
                favorites: favorites,
                selectedUserFavoriteGuid: undefined,
                selectedZoneFavoriteGuid: undefined,
                showDeleteFavoriteDialog: false,
            });
            if (match!.default) this.props.onDefaultFavoriteChange?.();
        }
    };

    getSelectedFavoriteDescription = () => {
        const match = this.state.favorites.find(
            (x) => x.guid === this.state.selectedUserFavoriteGuid
        );
        if (match) return match.description || '';
        else return '';
    };

    getSelectedFavoriteName = () => {
        const match = this.state.favorites.find(
            (x) => x.guid === this.state.selectedUserFavoriteGuid
        );
        if (match) return match.name || '';
        else return '';
    };

    allowZoneFavorites = () => {
        if (
            (this.props.type.id === FavoriteTypesEnum.EasyUpdate ||
                this.props.type.id == FavoriteTypesEnum.Query) &&
            Authorization.userHasRight(
                UserRightsEnum.AddZoneFavorites,
                this.props.user,
                this.props.zoneContext.zoneGuid
            )
        )
            return true;
        return false;
    };

    allowSetAsDefault = () => {
        const allowDefaultTypes = [
            FavoriteTypesEnum.MyAlertList,
            FavoriteTypesEnum.ZoneAlertList,
            FavoriteTypesEnum.CaseList,
        ];

        const allowDefault = allowDefaultTypes.includes(this.props.type.id);

        return allowDefault && this.state.newFavorite?.favoriteScope === FavoriteScopeEnum.User;
    };

    getFavoriteScopeOptions = (): IMultiSelectOption[] => {
        return [
            {
                label: 'User Favorite',
                value: FavoriteScopeEnum.User.toString(),
                id: FavoriteScopeEnum.User,
            },
            {
                label: 'Zone Favorite',
                value: FavoriteScopeEnum.Zone.toString(),
                id: FavoriteScopeEnum.Zone,
            },
        ];
    };

    getSelectedFavoriteScopeValue = () => {
        if (!this.state.newFavorite) {
            return;
        }

        switch (this.state.newFavorite.favoriteScope) {
            case FavoriteScopeEnum.User:
                return {
                    label: 'User Favorite',
                    value: FavoriteScopeEnum.User.toString(),
                    id: FavoriteScopeEnum.User,
                };
            case FavoriteScopeEnum.Zone:
                return {
                    label: 'Zone Favorite',
                    value: FavoriteScopeEnum.Zone.toString(),
                    id: FavoriteScopeEnum.Zone,
                };
            default:
                break;
        }
    };

    getDataScopeOptions = (): IMultiSelectOption[] => {
        return [
            {
                label: DataScopesEnum.National.DisplayName,
                value: DataScopesEnum.National.Value.toString(),
                id: DataScopesEnum.National.Value,
            },
            {
                label: DataScopesEnum.LocalBasic.DisplayName,
                value: DataScopesEnum.LocalBasic.Value.toString(),
                id: DataScopesEnum.LocalBasic.Value,
            },
        ];
    };

    getSelectedDataScopeValue = () => {
        if (!this.state.newFavorite) {
            return;
        }

        const dataScopeId = this.state.newFavorite.dataScope?.id ?? 0;

        switch (dataScopeId) {
            case DataScopesEnum.National.Value:
                return {
                    label: DataScopesEnum.National.DisplayName,
                    value: DataScopesEnum.National.Value.toString(),
                    id: DataScopesEnum.National.Value,
                };
            case DataScopesEnum.LocalBasic.Value:
                return {
                    label: DataScopesEnum.LocalBasic.DisplayName,
                    value: DataScopesEnum.LocalBasic.Value.toString(),
                    id: DataScopesEnum.LocalBasic.Value,
                };
            case -1:
                return { label: 'All', value: '-1', id: -1 };
            default:
                break;
        }
    };

    getFavoriteOptions = (values: IFavoriteModel[]): IMultiSelectOption[] => {
        return values.map((value, idx) => ({
            label: value.name!,
            value: value.name!,
            id: idx,
            guid: value.guid!,
        }));
    };

    getSelectedFavoriteValue = (guid: string, favoriteScope: FavoriteScopeEnum) => {
        const match = this.state.favorites.find((x) => x.guid === guid);

        if (match && this.state.selectedFavoriteScope === favoriteScope) {
            return { label: match.name!, value: match.name!, id: 0, guid: match.guid! };
        } else {
            return { label: '', value: '', id: -1, guid: '' };
        }
    };

    handleSaveFavoriteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (typeof this.props.onValidateFilters === 'function') {
            const hasError = !this.props.onValidateFilters();
            if (hasError) return;
        }

        this.setState({
            showAddNewModal: true,
            newFavorite: {
                type: this.props.type,
                settingJson: this.props.currentFavoriteSettingJson,
                favoriteScope: FavoriteScopeEnum.User,
            },
        });
    };

    handleCheckmateSelectChange = (selectedItem: any, name: string, subKey?: number) => {
        let newFavoriteCopy = cloneDeep(this.state.newFavorite);
        const validationCopy = this.state.validation;

        if (!newFavoriteCopy) {
            newFavoriteCopy = {
                type: this.props.type,
                settingJson: this.props.currentFavoriteSettingJson,
                favoriteScope: FavoriteScopeEnum.User,
            };
        }

        const valueId = selectedItem ? selectedItem.id : 0;
        const valueGuid =
            selectedItem && selectedItem.guid && selectedItem.guid != EmptyGuid
                ? selectedItem.guid
                : '';

        switch (name) {
            case 'favoriteScope':
                if (valueId) {
                    validationCopy.favoriteScope = [''];
                }

                newFavoriteCopy.favoriteScope = valueId;
                this.setState({ newFavorite: newFavoriteCopy, validation: validationCopy });
                break;
            case 'dataScope':
                if (valueId) {
                    newFavoriteCopy.dataScope = { id: valueId };
                } else {
                    newFavoriteCopy.dataScope = undefined;
                }

                this.setState({ newFavorite: newFavoriteCopy, validation: validationCopy });
                break;
            case 'favorites':
                if (valueGuid) {
                    validationCopy.favorites = [''];
                }

                this.setState({
                    selectedUserFavoriteGuid: subKey === FavoriteScopeEnum.User ? valueGuid : '',
                    selectedZoneFavoriteGuid: subKey === FavoriteScopeEnum.Zone ? valueGuid : '',
                    selectedFavoriteScope: subKey,
                    validation: validationCopy,
                });

                if (this.props.onFavoriteSelected) {
                    let settingJson = '';
                    if (valueGuid) {
                        const match = this.state.favorites.find((x) => x.guid === valueGuid);
                        if (match && match.settingJson) settingJson = match.settingJson;
                    }
                    this.props.onFavoriteSelected(settingJson);
                }
                break;
            default:
                break;
        }
    };

    render() {
        return (
            <span>
                <button
                    className="btn btn-no-bg float-end text-gray"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        this.setState({ showFavoritesModal: true });
                    }}
                >
                    <i className="fal fa-heart" />
                    &nbsp;Favorites
                </button>
                <button
                    className="btn btn-no-bg float-end text-gray"
                    onClick={this.handleSaveFavoriteClick}
                >
                    <i className="fal fa-heart-square" />
                    &nbsp;Save Favorite View
                </button>
                {this.state.showAddNewModal && this.state.newFavorite && (
                    <Modal
                        centered
                        size="lg"
                        show={true}
                        backdrop
                        dialogClassName="board-settings-dialog"
                    >
                        <Modal.Header>
                            <Modal.Title> Save Favorite View </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="validation-error-message">
                                <span className="text-danger">{this.state.validation.model}</span>
                            </div>
                            <div className="container-fluid">
                                {this.allowZoneFavorites() ? (
                                    <div className="row mb-2">
                                        <label className="control-label col-sm-2" htmlFor="type">
                                            Type*
                                        </label>
                                        <div className="col-sm-4">
                                            <CheckmateSelect
                                                options={this.getFavoriteScopeOptions()}
                                                name="favoriteScope"
                                                value={this.getSelectedFavoriteScopeValue()}
                                                onChange={(e: any) => {
                                                    this.handleCheckmateSelectChange(
                                                        e,
                                                        'favoriteScope'
                                                    );
                                                }}
                                            />
                                        </div>
                                        <div className="text-danger col-sm-6">
                                            {this.state.validation.favoriteScope}
                                        </div>
                                    </div>
                                ) : null}
                                {this.state.newFavorite.favoriteScope === FavoriteScopeEnum.Zone ? (
                                    <div className="row mb-2">
                                        <label
                                            className="control-label col-sm-2"
                                            htmlFor="dataScope"
                                        >
                                            Data Scope*
                                        </label>
                                        <div className="col-sm-4">
                                            <CheckmateSelect
                                                options={this.getDataScopeOptions()}
                                                name="dataScope"
                                                value={this.getSelectedDataScopeValue()}
                                                onChange={(e: any) => {
                                                    this.handleCheckmateSelectChange(
                                                        e,
                                                        'dataScope'
                                                    );
                                                }}
                                            />
                                        </div>
                                        <div className="text-danger col-sm-6">
                                            {this.state.validation.dataScope}
                                        </div>
                                    </div>
                                ) : null}
                                <div className="row mb-2">
                                    <label className="control-label col-sm-2" htmlFor="name">
                                        Name*
                                    </label>
                                    <div className="col-sm-10">
                                        <input
                                            className="form-control d-inline-block"
                                            type="text"
                                            maxLength={200}
                                            name="name"
                                            value={this.state.newFavorite.name ?? ''}
                                            onChange={this.handleChangeValue}
                                        />
                                        {this.allowSetAsDefault() ? (
                                            <span className="btn btn-no-bg text-gray">
                                                <input
                                                    name="chkDefault"
                                                    type="checkbox"
                                                    className="form-check-input d-inline-block"
                                                    onChange={this.handleCheckedChanged}
                                                    checked={this.state.newFavorite.default}
                                                />
                                                &nbsp;&nbsp;Set as Default View
                                            </span>
                                        ) : null}
                                        <span className="text-danger">
                                            <br></br>
                                            &nbsp;{this.state.validation.name}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="control-label" htmlFor="description">
                                        Description
                                    </label>
                                    <span className="text-danger">
                                        &nbsp;{this.state.validation.description}
                                    </span>
                                    <textarea
                                        className="form-control"
                                        value={this.state.newFavorite.description ?? ''}
                                        name="description"
                                        rows={4}
                                        maxLength={2000}
                                        onChange={this.handleChangeValue}
                                    />
                                </div>
                            </div>
                            <div className="dialog-btn-div margin-top-sm">
                                <button
                                    className="btn btn-default float-end"
                                    onClick={() =>
                                        this.setState({
                                            showAddNewModal: false,
                                            newFavorite: undefined,
                                            validation: {},
                                        })
                                    }
                                >
                                    Cancel
                                </button>
                                <button className="btn btn-orange float-end" onClick={this.addNew}>
                                    Save
                                </button>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
                {this.state.showFavoritesModal && (
                    <Modal
                        centered
                        show={this.state.showFavoritesModal}
                    >
                        <Modal.Header>
                            <Modal.Title>Favorites</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="padding-bottom-med">
                                <span className="text-danger">
                                    {this.state.validation.favorites}
                                </span>
                            </div>
                            {this.state.favorites.some(
                                (x) => x.favoriteScope === FavoriteScopeEnum.Zone
                            ) && (
                                <div className="row mb-2">
                                    <label className="control-label col-sm-3 pt-1">
                                        Zone Favorites
                                    </label>
                                    <div className="col-sm-7">
                                        <CheckmateSelect
                                            options={this.getFavoriteOptions(
                                                this.state.favorites.filter(
                                                    (x) =>
                                                        x.favoriteScope === FavoriteScopeEnum.Zone
                                                )
                                            )}
                                            name="favorites"
                                            value={this.getSelectedFavoriteValue(
                                                this.state.selectedZoneFavoriteGuid || '',
                                                FavoriteScopeEnum.Zone
                                            )}
                                            onChange={(e: any) => {
                                                this.handleCheckmateSelectChange(
                                                    e,
                                                    'favorites',
                                                    FavoriteScopeEnum.Zone
                                                );
                                            }}
                                        />
                                    </div>
                                    {this.state.selectedZoneFavoriteGuid &&
                                        this.props.currentFavoriteDetailsString &&
                                        this.state.favorites.find(
                                            (x) =>
                                                x.guid === this.state.selectedZoneFavoriteGuid &&
                                                x.favoriteScope === FavoriteScopeEnum.Zone
                                        ) && (
                                            <button
                                                className="col-sm-1 btn btn-no-bg"
                                                onClick={() =>
                                                    this.setState({
                                                        showFavoriteDetailsDialog: true,
                                                    })
                                                }
                                            >
                                                <i className="fal fa-lg fa-check-circle color-gray" />
                                            </button>
                                        )}
                                </div>
                            )}
                            <div className="row">
                                <label className="control-label col-sm-3 pt-1">
                                    User Favorites
                                </label>
                                <div className="col-sm-7">
                                    <CheckmateSelect
                                        options={this.getFavoriteOptions(
                                            this.state.favorites.filter(
                                                (x) => x.favoriteScope === FavoriteScopeEnum.User
                                            )
                                        )}
                                        name="favorites"
                                        value={this.getSelectedFavoriteValue(
                                            this.state.selectedUserFavoriteGuid || '',
                                            FavoriteScopeEnum.User
                                        )}
                                        onChange={(e: any) => {
                                            this.handleCheckmateSelectChange(
                                                e,
                                                'favorites',
                                                FavoriteScopeEnum.User
                                            );
                                        }}
                                    />
                                </div>
                                {this.state.selectedUserFavoriteGuid &&
                                    this.props.currentFavoriteDetailsString &&
                                    this.state.favorites.find(
                                        (x) =>
                                            x.guid === this.state.selectedUserFavoriteGuid &&
                                            x.favoriteScope === FavoriteScopeEnum.User
                                    ) && (
                                        <button
                                            className="col-sm-1 btn btn-no-bg"
                                            onClick={() =>
                                                this.setState({ showFavoriteDetailsDialog: true })
                                            }
                                        >
                                            <i className="fal fa-lg fa-check-circle color-gray" />
                                        </button>
                                    )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                className="btn btn-orange"
                                onClick={this.handleFavoriteSelected}
                            >
                                Select
                            </button>
                            <button
                                className="btn btn-default"
                                onClick={() =>
                                    this.setState({
                                        showFavoritesModal: false,
                                        validation: {},
                                    })
                                }
                            >
                                Cancel
                            </button>
                            {this.enableDelete() && (
                                <button
                                    className="btn btn-no-bg"
                                    onClick={() =>
                                        this.setState({ showDeleteFavoriteDialog: true })
                                    }
                                >
                                    <i className="fal fa-lg fa-trash-alt" />
                                </button>
                            )}
                        </Modal.Footer>
                    </Modal>
                )}

                {this.state.showFavoriteDetailsDialog && (
                    <Modal
                        centered
                        show={this.state.showFavoriteDetailsDialog}
                        onHide={() => this.setState({ showFavoriteDetailsDialog: false })}
                    >
                        <Modal.Header>
                            <Modal.Title> {this.getSelectedFavoriteName()} </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {(this.state.selectedUserFavoriteGuid ||
                                this.state.selectedZoneFavoriteGuid) &&
                                this.props.currentFavoriteDetailsString
                                    ?.replace(/\\n/gi, '\n')
                                    .split('\n')
                                    .map((item, key) => {
                                        return (
                                            <span key={key}>
                                                {item}
                                                <br />
                                            </span>
                                        );
                                    })}

                            {this.getSelectedFavoriteDescription() && (
                                <div className="mb-4">
                                    <label className="control-label" htmlFor="description">
                                        DESCRIPTION
                                    </label>
                                    <div>{this.getSelectedFavoriteDescription()}</div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                className="btn btn-black"
                                onClick={() =>
                                    this.setState({
                                        showFavoriteDetailsDialog: false,
                                        validation: {},
                                    })
                                }
                            >
                                OK
                            </button>
                        </Modal.Footer>
                    </Modal>
                )}

                <CheckmateDialog
                    body="Are you sure you want to delete this favorite? This operation is permanent and cannot be reverted back."
                    cancelText="Cancel"
                    closeButtonClassName="btn btn-default"
                    confirmButtonClassName="btn btn-black"
                    confirmText="Delete"
                    dialogClassName="favorite-dialog"
                    handleClose={() => this.setState({ showDeleteFavoriteDialog: false })}
                    handleConfirm={this.delete}
                    isShowingModal={this.state.showDeleteFavoriteDialog || false}
                    title="Delete"
                />
            </span>
        );
    }
}
